import type { AlertErrorHandlerFactory } from '@/features/shell'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { action, observable } from 'mobx'
import { task } from 'mobx-task'
import { mustHaveMaxLength, mustHaveMinLength } from '@/utils/validx-util'
import { required, validationContext } from 'validx'
import { type InfoGatheringStore } from '@/features/jobs/info-gathering/InfoGatheringStore'
import { Id } from '@/infra/id'

/**
 * View model for the dialog editing a job's notes supporting item.
 */
export class NotesDialogViewModel {
  private _itemId: Id = Id.none

  /**
   * The dialog's modal state.
   */
  readonly modal = new ModalViewModel()

  /**
   * The notes being edited.
   */
  @observable accessor text = ''

  /**
   * The allowed length of the notes.
   */
  readonly fieldLengths = {
    text: { min: 1, max: 5000 },
  } as const

  /**
   * Validation context.
   */
  readonly validation = validationContext(this, {
    text: [
      required(
        `Notes must be between ${this.fieldLengths.text.min} and ${this.fieldLengths.text.max} characters`,
      ),
      mustHaveMinLength(
        this.fieldLengths.text.min,
        `Notes must be between ${this.fieldLengths.text.min} and ${this.fieldLengths.text.max} characters`,
      ),
      mustHaveMaxLength(
        this.fieldLengths.text.max,
        `Notes must be between ${this.fieldLengths.text.min} and ${this.fieldLengths.text.max} characters`,
      ),
    ],
  })

  constructor(
    private readonly _jobId: Id,
    private readonly _infoGatheringStore: InfoGatheringStore,
    private readonly _canEdit: () => boolean,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {}

  /**
   * Validates the notes.
   */
  @action.bound
  validate() {
    return this.validation.reset().validate().isValid
  }

  /**
   * Clears the notes.
   */
  @action.bound
  reset() {
    this.text = ''
  }

  /**
   * Shows the dialog for the notes supporting item, starting from its text.
   */
  @action.bound
  show(itemId: Id, text: string) {
    this._itemId = itemId
    this.text = text
    this.validation.reset()
    this.modal.show()
  }

  /**
   * Saves the notes when the user may edit them.
   */
  submit = task.resolved(async () => {
    if (!this._canEdit() || !this.validate()) {
      return
    }

    await this._infoGatheringStore
      .provideSupportingItemNotes({
        jobId: this._jobId,
        itemId: this._itemId,
        notes: this.text,
      })
      .catch(this._createAlertErrorHandler('Saving the notes failed'))
    this.reset()

    this.modal.close()
  })
}
